import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, AfterViewInit, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { ToastrService } from 'ngx-toastr';
import { NbDialogRef } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-user-card-link',
  templateUrl: './user-card-link.component.html',
  styleUrls: ['./user-card-link.component.scss']
})
export class UserCardLinkComponent implements OnInit, AfterViewInit {
  @Input() cardUID!: string;
  @Input() storeId!: string;
  @Input() selectedUser: any = null;
  @ViewChild('signaturePadCanvas') canvasEl!: ElementRef;
  
  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  
  // Step management
  currentStep: 'search' | 'verify' | 'consent' | 'success' = 'search';
  
  // User search & visits
  searchForm = new FormGroup({
    searchTerm: new FormControl('', [Validators.minLength(2)])
  });
  users: any[] = [];
  filteredUsers: any[] = [];
  recentVisits: any[] = [];
  loading: boolean = false;
  error: string | undefined = undefined;
  
  // Time window for recent visits
  timeWindowMinutes: number = 30;
  
  // Signature
  signatureData: string | undefined = undefined;
  signatureIsEmpty: boolean = true;
  signatureConfirmed: boolean = false;
  signatureRequired: boolean = true;
  
  constructor(
    private service: ApiService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogRef: NbDialogRef<UserCardLinkComponent>,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // If we have a selected user, use them directly
    if (this.selectedUser) {
      // No need to fetch from API since we already have the user
      console.log('Using pre-selected user:', this.selectedUser);
      this.currentStep = 'verify';
    } else {
      // Otherwise, load recent visitors as usual
      this.loadRecentVisitors();
    }
    
    // Subscribe to changes in the search term
    this.searchForm.get('searchTerm')?.valueChanges.subscribe(value => {
      if (value && value.length >= 2) {
        this.filterUsers(value);
      } else if (!value) {
        this.filteredUsers = [...this.users];
      }
    });
  }
  
  ngAfterViewInit(): void {
    if (this.currentStep === 'consent') {
      this.initCanvas();
    }
  }
  
  // Initialize canvas for signature
  private initCanvas(): void {
    setTimeout(() => {
      const canvas = document.getElementById('signaturePadCanvas') as HTMLCanvasElement;
      if (!canvas) {
        console.error('Canvas element not found');
        return;
      }
      
      this.ctx = canvas.getContext('2d');
      if (!this.ctx) {
        console.error('Could not get canvas context');
        return;
      }
      
      console.log('Initializing canvas for signature');
      
      // Set initial canvas state
      this.ctx.fillStyle = '#f5f5f5';
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = 'black';
      
      // Mouse events
      canvas.addEventListener('mousedown', (e) => this.startDrawing(e, canvas));
      canvas.addEventListener('mousemove', (e) => this.draw(e, canvas));
      canvas.addEventListener('mouseup', () => this.stopDrawing(canvas));
      canvas.addEventListener('mouseout', () => this.stopDrawing(canvas));
      
      // Touch events
      canvas.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (e.touches.length > 0) {
          this.startDrawingTouch(e.touches[0], canvas);
        }
      });
      
      canvas.addEventListener('touchmove', (e) => {
        e.preventDefault();
        if (e.touches.length > 0 && this.isDrawing) {
          this.drawTouch(e.touches[0], canvas);
        }
      });
      
      canvas.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.stopDrawing(canvas);
      });
      
      console.log('Canvas event listeners initialized');
    }, 200);
  }
  
  // Start drawing on mouse down
  private startDrawing(e: MouseEvent, canvas: HTMLCanvasElement): void {
    this.isDrawing = true;
    this.signatureIsEmpty = false;
    
    if (this.ctx) {
      const rect = canvas.getBoundingClientRect();
      this.ctx.beginPath();
      this.ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    }
    
    this.cdr.detectChanges();
  }
  
  // Draw on mouse move
  private draw(e: MouseEvent, canvas: HTMLCanvasElement): void {
    if (!this.isDrawing || !this.ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    this.ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    this.ctx.stroke();
  }
  
  // Start drawing on touch
  private startDrawingTouch(touch: Touch, canvas: HTMLCanvasElement): void {
    this.isDrawing = true;
    this.signatureIsEmpty = false;
    
    if (this.ctx) {
      const rect = canvas.getBoundingClientRect();
      this.ctx.beginPath();
      this.ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
    }
    
    this.cdr.detectChanges();
  }
  
  // Draw on touch move
  private drawTouch(touch: Touch, canvas: HTMLCanvasElement): void {
    if (!this.isDrawing || !this.ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    this.ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
    this.ctx.stroke();
  }
  
  // Stop drawing
  private stopDrawing(canvas: HTMLCanvasElement): void {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.signatureData = canvas.toDataURL();
      this.cdr.detectChanges();
    }
  }
  
  // Navigation between steps
  goToConsentStep(): void {
    this.currentStep = 'consent';
    
    // Initialize canvas in the next tick
    setTimeout(() => {
      this.initCanvas();
    }, 200);
  }
  
  // Load users who recently visited the store
  loadRecentVisitors(): void {
    if (!this.storeId) {
      this.toastr.error('Store ID is required');
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    // Calculate time window (30 minutes ago from now)
    const now = new Date();
    const startDate = new Date(now.getTime() - (this.timeWindowMinutes * 60 * 1000));
    
    // Format dates for the API
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = now.toISOString();
    
    // Construct API URL with store ID, start date, and end date
    const apiUrl = `${ApiUrl.getUserVisit}?page=1&startDate=${formattedStartDate}&endDate=${formattedEndDate}&storeId=${this.storeId}`;
    
    console.log('Loading recent visitors with API URL:', apiUrl);
    
    this.service.getRequest(apiUrl).subscribe(
      (res: any) => {
        console.log('Recent visitors API response:', res);
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.recentVisits = responseData.data?.storeData || [];
          this.users = this.recentVisits.map(visit => {
            // Map the response to a format similar to the user search results
            const userData = visit.userData || {};
            return {
              _id: userData._id,
              fullName: userData.name,
              email: userData.email,
              phone: userData.phone,
              visitTime: visit.visitTime,
              // Keep original data for reference
              originalVisitData: visit
            };
          });
          
          // Initialize filtered users with all users
          this.filteredUsers = [...this.users];
          
          console.log('Loaded recent visitors:', this.users.length);
          this.error = undefined;
        } else {
          this.users = [];
          this.filteredUsers = [];
          this.error = responseData.message || 'Failed to load recent visitors';
          this.toastr.error(this.error);
        }
        
        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        console.error('Error loading recent visitors:', err);
        this.users = [];
        this.filteredUsers = [];
        this.error = err.error?.message || 'Failed to load recent visitors';
        this.toastr.error(this.error);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }
  
  // Filter the already loaded visitors
  filterUsers(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredUsers = [...this.users];
      return;
    }
    
    searchTerm = searchTerm.toLowerCase().trim();
    
    this.filteredUsers = this.users.filter(user => {
      // Filter by name, email, or phone
      return (
        (user.fullName && user.fullName.toLowerCase().includes(searchTerm)) ||
        (user.email && user.email.toLowerCase().includes(searchTerm)) ||
        (user.phone && user.phone.includes(searchTerm))
      );
    });
    
    console.log(`Filtered users: ${this.filteredUsers.length} of ${this.users.length} match "${searchTerm}"`);
  }
  
  // Search in the already loaded visitors (for form submission)
  searchUsers(): void {
    const searchTerm = this.searchForm.get('searchTerm')?.value;
    
    if (searchTerm && searchTerm.length >= 2) {
      this.filterUsers(searchTerm);
    } else if (!searchTerm) {
      this.filteredUsers = [...this.users];
    } else {
      this.toastr.error('Please enter at least 2 characters to search');
    }
  }
  
  // User selects a visitor to link the card to
  selectUser(user: any): void {
    this.selectedUser = user;
    this.currentStep = 'verify';
  }
  
  // Navigation between steps
  goToVerifyStep(): void {
    this.currentStep = 'verify';
  }
  
  goBack(): void {
    if (this.currentStep === 'verify') {
      this.currentStep = 'search';
      this.selectedUser = null;
    } else if (this.currentStep === 'consent') {
      this.currentStep = 'verify';
      // Reset signature data
      this.signatureData = undefined;
      this.signatureIsEmpty = true;
      this.signatureConfirmed = false;
    }
  }
  
  // Close the dialog
  close(refreshData: boolean = false): void {
    this.dialogRef.close(refreshData);
  }
  
  // Check if signature is empty
  checkSignature(): void {
    const canvas = document.getElementById('signaturePadCanvas') as HTMLCanvasElement;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      
      // Check if any non-background pixels exist
      const hasData = Array.from(imageData).some((val, i) => i % 4 === 3 && val > 0);
      
      this.signatureIsEmpty = !hasData;
      
      if (!this.signatureIsEmpty) {
        this.signatureData = canvas.toDataURL();
      }
    } catch (e) {
      console.error('Error checking signature:', e);
    }
    
    this.cdr.detectChanges();
  }
  
  // Clear the signature
  clearSignature(): void {
    const canvas = document.getElementById('signaturePadCanvas') as HTMLCanvasElement;
    if (this.ctx && canvas) {
      this.ctx.fillStyle = '#f5f5f5';
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
      this.signatureIsEmpty = true;
      this.signatureConfirmed = false;
      this.signatureData = undefined;
    }
  }
  
  // Confirm the signature
  confirmSignature(): void {
    if (this.signatureIsEmpty || !this.signatureData) {
      this.toastr.error('Please provide a signature before confirming');
      return;
    }
    
    this.signatureConfirmed = true;
    this.toastr.success('Signature confirmed');
  }
  
  // Submit the link request
  submitLinkRequest(): void {
    if (!this.selectedUser || !this.cardUID || !this.storeId) {
      this.toastr.error('Missing required information for link request');
      return;
    }

    if (this.signatureRequired && !this.signatureConfirmed) {
      this.toastr.warning('Please confirm the signature before submitting');
      return;
    }

    this.loading = true;
    this.spinner.show();
    this.error = '';

    // Prepare the link request data
    const requestData: any = {
      userId: this.selectedUser._id,
      cardUID: this.cardUID,
      storeId: this.storeId
    };

    // Add signature data if provided
    if (this.signatureRequired && this.signatureData) {
      requestData.signature = this.signatureData;
    }

    // Log the request data for debugging
    console.log('Submitting link request with data:', {
      cardUID: requestData.cardUID,
      userId: requestData.userId,
      storeId: requestData.storeId,
      signatureProvided: !!requestData.signature
    });
    
    // First create a store visit for this user
    this.createStoreVisit(requestData.userId, requestData.storeId).then(() => {
      // Then proceed with linking the card
      this.linkCardToUser(requestData);
    }).catch(err => {
      console.error('Error creating store visit:', err);
      // Continue with card linking even if visit creation fails
      this.linkCardToUser(requestData);
    });
  }

  private createStoreVisit(userId: string, storeId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const visitData = {
        userId: userId,
        storeId: storeId
      };
      
      this.service.postRequest(ApiUrl.createStoreVisit, visitData).subscribe(
        (res: any) => {
          console.log('Store visit created:', res);
          resolve();
        },
        (err: any) => {
          console.error('Failed to create store visit:', err);
          reject(err);
        }
      );
    });
  }

  private linkCardToUser(requestData: any) {
    this.service.postRequest(ApiUrl.linkCardToUser, requestData).subscribe(
      (res: any) => {
        console.log('Link card response:', res);
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.toastr.success('Card successfully linked to user account');
          this.currentStep = 'success';
        } else {
          this.error = responseData.message || 'Failed to link card to user';
          this.toastr.error(this.error);
        }
        
        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        console.error('Error linking card:', err);
        this.error = err.error?.message || 'Failed to link card to user';
        this.toastr.error(this.error);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }
} 